import React, { useMemo, useRef, useState, memo, useCallback } from 'react';
import { useTheme } from './useTheme';
import type { BreakpointDef, GridLayoutConfig, LayoutItem, Config } from '../Config/config';
import { getRegisteredComponent } from './ComponentRegistry';
import GridOverlay from './GridOverlay';
import GridInteractive from './GridInteractive';
import GridControls from './GridControls';
import ConfigEditor from './ConfigEditor';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSimulatedBreakpoint } from './hooks/useBreakpoint';
import { useLazyComponent } from '../Hooks/useLazyComponent';
import { isDevModeEnabled } from '../Config/devMode';

function computeStyleForItem(item: LayoutItem, bpName: BreakpointDef['name']) {
  const pos = item.positions?.[bpName];
  const style: React.CSSProperties = {};
  if (pos?.colStart) style.gridColumnStart = String(pos.colStart);
  if (pos?.colEnd) style.gridColumnEnd = String(pos.colEnd);
  if (pos?.rowStart) style.gridRowStart = String(pos.rowStart);
  if (pos?.rowEnd) style.gridRowEnd = String(pos.rowEnd);
  return style;
}

// Composant mémorisé pour les éléments de grille pour optimiser les performances
const GridItem = memo<{
  item: LayoutItem;
  bpName: BreakpointDef['name'];
  globalParams: Record<string, unknown>;
  instanceParams: Record<string, unknown>;
  lazyLoadEnabled?: boolean;
  holdImagesUntilApiReady?: boolean;
}>(({ item, bpName, globalParams, instanceParams, lazyLoadEnabled = true, holdImagesUntilApiReady = false }) => {
  const isImageModule = item.component === 'ImageModule';
  
  const itemStyle = computeStyleForItem(item, bpName);
  const mergedProps = { ...globalParams, ...instanceParams };
  
  // Utiliser le hook de lazy loading avec distance adaptative
  const { isVisible, ref, preloadDistance } = useLazyComponent({
    useAdaptiveDistance: true,  // Activer la distance adaptative
    threshold: 0.01,
    enabled: lazyLoadEnabled,
    forceLoadAll: true,         // Charger toute la page progressivement
    sequentialDelay: 100        // 100ms entre chaque composant
  });

  // Tant que l’API n’a pas répondu (instances non chargées), on évite d’afficher l’image de la config
  // (sinon flicker vite.svg -> image API). On affiche un placeholder "Chargement...".
  if (holdImagesUntilApiReady && isImageModule) {
    return (
      <div key={item.id} className="gc-grid-item" style={itemStyle} ref={ref}>
        <div style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.04)',
          borderRadius: '4px',
          color: 'rgba(0,0,0,0.65)',
          fontSize: '14px',
          fontWeight: 600
        }}>
          Chargement...
        </div>
      </div>
    );
  }
  
  const Cmp = getRegisteredComponent(item.component);
  if (!Cmp) return null;
  
  return (
    <div key={item.id} className="gc-grid-item" style={itemStyle} ref={ref}>
      <div style={{ 
        width: '100%', 
        height: '100%', 
        maxWidth: '100%',
        maxHeight: '100%',
        overflow: 'auto',
        boxSizing: 'border-box'
      }}>
        {isVisible ? (
          <Cmp key={`${item.id}-${JSON.stringify(mergedProps)}`} {...mergedProps} />
        ) : (
          <div className="skeleton-loader" style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderRadius: '4px',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}>
            {/* Indicateur de préchargement en dev mode */}
            {process.env.NODE_ENV === 'development' && preloadDistance && (
              <div style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                background: 'rgba(138, 92, 246, 0.8)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 'bold'
              }}>
                ⚡ {preloadDistance}px
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export const GridContainer: React.FC<{ layout?: GridLayoutConfig; apiLoading?: boolean }> = ({ layout, apiLoading = false }) => {
  const { config, resolveLayoutItems, actions } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const effectiveLayout = layout ?? config.gridLayout;
  const gridRef = useRef<HTMLDivElement>(null);
  const [interactiveRows, setInteractiveRows] = useState<number>(0);
  const [overlayVisible, setOverlayVisible] = useState<boolean>(true);
  const [selectionEnabled, setSelectionEnabled] = useState<boolean>(true);
  const [configEditorOpen, setConfigEditorOpen] = useState<boolean>(false);
  const [selectedPosition, setSelectedPosition] = useState<{ colStart: number; colEnd: number; rowStart: number; rowEnd: number } | null>(null);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);
  const [simulatedWidth, setSimulatedWidth] = useState<number | undefined>(undefined);

  const { currentBreakpoint: bp, isSimulating } = useSimulatedBreakpoint(effectiveLayout.breakpoints, simulatedWidth);
  
  // Désactiver automatiquement l'overlay si trop de colonnes pour éviter le lag
  const shouldDisableOverlay = bp.columns > 12;
  
  const devModeEnabled = isDevModeEnabled(config);
  
  // Vérifier si l'overlay est activé
  // IMPORTANT: Si la config globale a overlay: false, désactiver sur TOUTES les pages
  const globalOverlayEnabled = config.gridLayout.overlay?.enabled === true;
  const isOverlayEnabled = devModeEnabled && globalOverlayEnabled && (effectiveLayout.overlay?.enabled === true);
  
  // Fermer automatiquement l'éditeur si l'overlay est désactivé
  React.useEffect(() => {
    if (!isOverlayEnabled && configEditorOpen) {
      setConfigEditorOpen(false);
    }
  }, [isOverlayEnabled, configEditorOpen]);
  
  const handleConfigChange = useCallback((newConfig: Partial<Config>) => {
    actions.updateConfig(newConfig);
  }, [actions]);
  
  const handleApplyPosition = (componentId: string) => {
    if (!selectedPosition) return;
    
    // Trouver la page actuelle
    const currentPage = config.PagesList.find(page => page.pageUrl === location.pathname);
    
    if (currentPage?.gridLayout) {
      // Appliquer à la grille personnalisée de la page
      const newPageItems = currentPage.gridLayout.items.map(item => {
        if (item.id === componentId) {
          return {
            ...item,
            positions: {
              ...item.positions,
              [bp.name]: selectedPosition
            }
          };
        }
        return item;
      });
      
      const newPages = config.PagesList.map(p => 
        p.pageUrl === location.pathname 
          ? { 
              ...p, 
              gridLayout: { 
                ...p.gridLayout, 
                items: newPageItems,
                breakpoints: p.gridLayout?.breakpoints ?? config.gridLayout.breakpoints
              } 
            }
          : p
      );
      
      actions.updateConfig({ PagesList: newPages });
    } else {
      // Appliquer à la grille globale
      const newItems = config.gridLayout.items.map(item => {
        if (item.id === componentId) {
          return {
            ...item,
            positions: {
              ...item.positions,
              [bp.name]: selectedPosition
            }
          };
        }
        return item;
      });
      
      actions.updateConfig({
        gridLayout: {
          ...config.gridLayout,
          items: newItems
        }
      });
    }
    
    setSelectedPosition(null);
    setSelectedComponentId(null);
  };

  // Trouver la page actuelle
  const currentPage = config.PagesList.find(page => page.pageUrl === location.pathname);
  
  const selectedComponent = selectedComponentId 
    ? (() => {
        // Chercher d'abord dans la grille personnalisée de la page
        if (currentPage?.gridLayout) {
          return currentPage.gridLayout.items.find(i => i.id === selectedComponentId);
        }
        // Sinon chercher dans la grille globale
        return config.gridLayout.items.find(i => i.id === selectedComponentId);
      })()
    : null;

  const { insideItems, outsideItems } = useMemo(() => {
    const resolved = resolveLayoutItems(effectiveLayout.items);
    const inside = resolved.filter(i => !i.outsideGrid);
    const outside = resolved.filter(i => i.outsideGrid);
    return { insideItems: inside, outsideItems: outside };
  }, [effectiveLayout, resolveLayoutItems]);

  // style de la grille en fonction du breakpoint courant
  // Cases carrées : largeur effective / columns = largeur d'une case = hauteur d'une case
  const effectiveWidth = isSimulating && simulatedWidth ? simulatedWidth : window.innerWidth;
  const gridStyle: React.CSSProperties = {
    gridTemplateColumns: `repeat(${bp.columns}, 1fr)`,
    columnGap: `${effectiveLayout.gap ?? 0}px`,
    rowGap: `${effectiveLayout.rowGap ?? effectiveLayout.gap ?? 0}px`,
    gridAutoRows: `calc((${effectiveWidth}px - ${effectiveLayout.gap ?? 0}px * ${bp.columns - 1}) / ${bp.columns})`, // Cases carrées
    gridAutoFlow: 'row' // Pas de "dense" pour respecter les positions explicites
  };

  const holdImagesUntilApiReady = apiLoading && config.api.enableSync && config.api.autoLoadParams;

  return (
    <>
      {/* Outils dev (contrôles + éditeur) - seulement si le dev mode ET l'overlay sont activés */}
      {devModeEnabled && isOverlayEnabled && (
        <>
          <GridControls
            overlayVisible={overlayVisible}
            selectionEnabled={selectionEnabled}
            onToggleOverlay={() => setOverlayVisible(!overlayVisible)}
            onToggleSelection={() => setSelectionEnabled(!selectionEnabled)}
            onOpenConfigEditor={() => setConfigEditorOpen(true)}
            isSelecting={isSelecting}
          />
          
          {configEditorOpen && (
            <ConfigEditor
              config={config}
              onConfigChange={handleConfigChange}
              currentBreakpoint={bp.name}
              selectedComponent={selectedComponent}
              selectedPosition={selectedPosition}
              onApplyPosition={handleApplyPosition}
              onClose={() => setConfigEditorOpen(false)}
              onSelectComponentForMove={setSelectedComponentId}
              isSelecting={isSelecting}
              currentPageUrl={location.pathname}
              onNavigateToPage={(url) => navigate(url)}
              onSimulationChange={setSimulatedWidth}
            />
          )}
        </>
      )}
      

      {/* Elements hors grille, rendus avant pour typiquement navbar, overlays etc. */}
      {outsideItems.map(item => {
        if (holdImagesUntilApiReady && item.component === 'ImageModule') {
          return (
            <div
              key={item.id}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderRadius: '6px',
                color: 'rgba(0,0,0,0.65)',
                fontWeight: 600
              }}
            >
              Chargement...
            </div>
          );
        }

        const Cmp = getRegisteredComponent(item.component);
        if (!Cmp) return null;
        // Utiliser les paramètres spécifiques à l'instance (item.props) avec fallback sur les paramètres globaux
        const globalParams = config.componentParams[item.component] || {};
        const instanceParams = item.props || {};
        const mergedProps = { ...globalParams, ...instanceParams };
        return <Cmp key={item.id} {...mergedProps} />;
      })}

      {/* Indicateur de simulation */}
      {isSimulating && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'rgba(255, 193, 7, 0.9)',
          color: '#000',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          border: '2px solid #ffc107'
        }}>
          📱 Simulation: {simulatedWidth}px ({bp.name.toUpperCase()}) - {bp.columns} colonnes
        </div>
      )}

      {/* Grille avec position relative pour contenir l'overlay */}
      <div ref={gridRef} className="gc-grid" style={{
        ...gridStyle, 
        position: 'relative',
        // Appliquer la largeur simulée si en mode simulation
        ...(isSimulating && simulatedWidth ? {
          maxWidth: `${simulatedWidth}px`,
          margin: '0 auto',
          border: '2px dashed #ffc107',
          borderRadius: '8px'
        } : {})
      }}>
        {insideItems.map(item => {
          const globalParams = config.componentParams[item.component] || {};
          const instanceParams = item.props || {};
          return (
            <GridItem
              key={item.id}
              item={item}
              bpName={bp.name}
              globalParams={globalParams}
              instanceParams={instanceParams}
              holdImagesUntilApiReady={holdImagesUntilApiReady}
            />
          );
        })}
        
        {/* Overlay dans la grille */}
        {devModeEnabled && isOverlayEnabled && (
          <>
            {/* Overlay visuel - EN DESSOUS pour ne pas bloquer les clics */}
            {overlayVisible && !shouldDisableOverlay && effectiveLayout.overlay && (
              <GridOverlay
                breakpoints={effectiveLayout.breakpoints}
                showColumns={effectiveLayout.overlay.showColumns}
                showRows={effectiveLayout.overlay.showRows}
                color={effectiveLayout.overlay.color}
                opacity={effectiveLayout.overlay.opacity}
                gap={effectiveLayout.gap ?? 0}
                gridRef={gridRef}
                interactiveRows={interactiveRows}
                isSelecting={isSelecting}
                simulatedWidth={simulatedWidth}
              />
            )}
            
            {/* Message d'information si overlay désactivé pour performance */}
            {shouldDisableOverlay && overlayVisible && (
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                backgroundColor: 'rgba(255, 193, 7, 0.9)',
                color: '#000',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                zIndex: 1000,
                border: '1px solid #ffc107'
              }}>
                ⚡ Overlay désactivé pour les performances ({bp.columns} colonnes)
              </div>
            )}
            
            {/* Grille interactive pour sélection - AU DESSUS pour capturer les clics */}
            {/* SEULEMENT si l'overlay est activé ET la sélection est activée */}
            {selectionEnabled && (
              <GridInteractive
                breakpoints={effectiveLayout.breakpoints}
                items={effectiveLayout.items}
                gap={effectiveLayout.gap ?? 0}
                gridRef={gridRef}
                onDisplayRowsChange={setInteractiveRows}
                onSelectionChange={setSelectedPosition}
                onComponentClick={setSelectedComponentId}
                onSelectingStateChange={setIsSelecting}
                simulatedWidth={simulatedWidth}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default GridContainer;


